/**
 * ChiffresTableView — tableau des chiffres (postes × exercices), rendu PARTAGÉ.
 *
 * lot2 — Source unique consommée par la fiche interne (`PropertyViewContent`) et la fiche
 * publique (`PropertyPublicPage`). iter133 avait refondu le tableau côté interne seulement :
 * la fiche publique était restée sur l'empilement par année. Une seule définition ici =
 * plus de divergence possible au prochain changement.
 *
 * Règles (identiques sur les deux fiches) :
 *   - années en colonnes (N-1 / N-2 / N-3), postes en lignes
 *   - une seule ligne CA hors hôtel ; ventilation hôtelière en sous-lignes
 *   - ligne entièrement vide masquée, cellule vide en « — »
 *   - EBE et résultat net sur les 3 exercices
 *   - tabular-nums + format FR
 */
import React from 'react';
import { formatPriceFR } from '../../utils/formatFR';

export const PERF_YEARS = [
  { key: 'n1', label: 'N-1' },
  { key: 'n2', label: 'N-2' },
  { key: 'n3', label: 'N-3' },
];

const HOTEL_POSTES = ['hebergement', 'restauration', 'bar', 'divers'];

/**
 * Construit les lignes du tableau depuis `exploitation.chiffres`.
 * `legacy` porte les replis INTERNES (anciens champs confidentiels) : la fiche publique
 * n'en passe aucun — elle ne voit que ce que la projection publique a laissé passer.
 */
export function buildChiffresRows(chiffres, legacy = {}) {
  const ch = chiffres || {};
  // La ventilation existe dès qu'UN poste d'UN exercice est renseigné (iter133).
  const hasHotelBreakdown = PERF_YEARS.some((y) => HOTEL_POSTES.some((p) => ch[`ca_${y.key}_${p}`] != null));
  const sommeVentilation = (annee) => {
    if (!hasHotelBreakdown) return null;
    const postes = HOTEL_POSTES.map((p) => ch[`ca_${annee}_${p}`]).filter((v) => v != null);
    return postes.length > 0 ? postes.reduce((a, b) => a + b, 0) : null;
  };

  return [
    ...(hasHotelBreakdown ? [
      { id: 'ca-hebergement', label: 'CA hébergement', field: 'hebergement' },
      { id: 'ca-restauration', label: 'CA restauration', field: 'restauration' },
      { id: 'ca-bar', label: 'CA bar', field: 'bar' },
      { id: 'ca-divers', label: 'CA divers', field: 'divers' },
    ].map((r) => ({ ...r, values: PERF_YEARS.map((y) => ch[`ca_${y.key}_${r.field}`] ?? null) })) : []),
    {
      id: 'ca-total',
      label: 'CA total',
      strong: true,
      // Hôtel : si le total d'un exercice n'a pas été saisi, on somme la ventilation
      // (affichage seulement) — sinon la colonne resterait vide alors que les postes sont là.
      values: [
        ch.ca_n1_total ?? legacy.ca_n1 ?? legacy.ca_annuel ?? sommeVentilation('n1'),
        ch.ca_n2_total ?? legacy.ca_n2 ?? sommeVentilation('n2'),
        ch.ca_n3_total ?? sommeVentilation('n3'),
      ],
    },
    {
      id: 'ebe',
      label: 'EBE',
      values: [ch.ebe_n1 ?? legacy.ebe ?? null, ch.ebe_n2 ?? null, ch.ebe_n3 ?? null],
    },
    {
      id: 'resultat-net',
      label: 'Résultat net',
      values: [ch.resultat_net_n1 ?? legacy.resultat_net ?? null,
        ch.resultat_net_n2 ?? null, ch.resultat_net_n3 ?? null],
    },
    {
      id: 'charges',
      label: 'Charges annuelles',
      values: PERF_YEARS.map((y) => ch[`charges_${y.key}`] ?? null),
    },
  ].filter((r) => r.values.some((v) => v != null));
}

export const ChiffresTableView = ({ rows, testId = 'perf-table' }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testId}>
      <thead>
        <tr className="text-slate-500">
          <th className="text-left font-medium pb-2"><span className="sr-only">Poste</span></th>
          {PERF_YEARS.map((y) => (
            <th key={y.key} className="text-right font-semibold pb-2 pl-6 text-[11px] uppercase tracking-wider">{y.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className="border-t border-slate-100" data-testid={`perf-row-${row.id}`}>
            <td className={`py-2.5 text-left ${row.strong ? 'font-bold text-slate-900' : 'text-slate-600'}`}>{row.label}</td>
            {row.values.map((v, i) => (
              <td
                key={PERF_YEARS[i].key}
                className={`py-2.5 pl-6 text-right tabular-nums ${row.strong ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'} ${v == null ? 'text-slate-300 font-normal' : ''}`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid={`perf-${row.id}-${PERF_YEARS[i].key}`}
              >
                {v == null ? '—' : formatPriceFR(v)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default ChiffresTableView;
